import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { customBlue } from "../../theme/palette";
import { InitialMicCheckState } from "./InitialMicCheck";
import { RecordingMicCheckState } from "./RecordingMicCheck";
import { SuccessMicCheckState } from "./SuccessMicCheck";
import { FailedMicCheckState } from "./FailedMicCheck";
import { NoAudioMicCheckState } from "./NoAudioMicCheck";
import { FinalMicCheckState } from "./FinalMicCheck";
import { TapIcon } from "../Icons/TapIcon";
import { HumanSpeaking } from "../Icons/HumanSpeaking";
import { useMicCheck } from "./miccheck-hook";
import { exitAssessment } from "../../lib/assessmentExit";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onRetry: () => void;
  submissionId: string | null;
  taskType?: "activity" | "benchmark";
}

export function MicCheckModal({
  open,
  onClose,
  onSuccess,
  onRetry,
  submissionId,
  taskType = "activity",
}: Props) {
  const {
    micStatus,
    audioDetected,
    liveRecordingFrequencyLevel,
    handleMicrophoneTap,
    handleListenToInstructions,
    handleListenToWaitInstructions,
    handleStopRecording,
    handleRetry,
    handleCancel,
    handleSkipMicCheck,
    handleAudioDetected,
    handleStartBenchmark,
    handleMicCheckComplete,
    taskType: currentTaskType,
  } = useMicCheck({
    submissionId,
    open,
    onClose,
    onSuccess,
    onRetry,
    taskType,
  });

  const handleBackToDashboard = () => {
    exitAssessment();
  };

  const renderMicrophoneIcon = () => {
    if (micStatus === "initial") {
      return <InitialMicCheckState onClick={handleMicrophoneTap} />;
    }

    if (micStatus === "recording") {
      return (
        <RecordingMicCheckState
          onCancel={handleCancel}
          onRetry={handleRetry}
          onStop={handleStopRecording}
          onAudioDetected={handleAudioDetected}
          liveRecordingFrequencyLevel={liveRecordingFrequencyLevel}
          audioDetected={audioDetected}
        />
      );
    }

    if (micStatus === "success") {
      return (
        <SuccessMicCheckState
          onStartBenchmark={handleStartBenchmark}
          taskType={currentTaskType}
        />
      );
    }

    if (micStatus === "failed") {
      return <FailedMicCheckState />;
    }

    if (micStatus === "no-audio") {
      return (
        <NoAudioMicCheckState
          onRetry={handleRetry}
          onClose={handleSkipMicCheck}
        />
      );
    }

    if (micStatus === "final") {
      return (
        <FinalMicCheckState
          onStart={handleMicCheckComplete}
          onBackToDashboard={handleBackToDashboard}
          onListenToWaitInstructions={handleListenToWaitInstructions}
        />
      );
    }

    return null;
  };

  return (
    <Dialog
      open={open}
      fullScreen={micStatus === "final"}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      sx={{
        ".MuiBackdrop-root": {
          backgroundColor:
            micStatus === "final" ? "rgba(0, 0, 0, 0.8)" : "#808080",
          ...(micStatus !== "final" && {
            backgroundImage:
              "linear-gradient(rgba(128, 128, 128, 0.8), rgba(128, 128, 128, 0.8)), url(/images/im-ready.png)",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center calc(35%)",
            backgroundSize: "auto 70%",
          }),
        },
        ".MuiDialog-container": {
          alignItems: micStatus === "final" ? "center" : "flex-start",
          paddingTop: micStatus === "final" ? 0 : "22vh",
        },
        ".MuiPaper-root.MuiDialog-paper": {
          overflow: "hidden",
          borderRadius: micStatus === "final" ? 0 : "12px",
          padding: micStatus === "final" ? 0 : "32px 1px 1px 32px",
          boxShadow:
            micStatus === "final"
              ? "none"
              : "0 25px 50px -12px rgb(0 0 0 / 0.25)",
          backgroundColor: "#ffffff",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          margin: 0,
          position: "relative",
          width:
            micStatus === "final"
              ? "100vw"
              : {
                  xs: "90vw",
                  sm: "80vw",
                  md: "650px",
                },
          height: micStatus === "final" ? "100vh" : "auto",
          maxWidth:
            micStatus === "final"
              ? "100vw"
              : {
                  xs: "90vw",
                  sm: "80vw",
                  md: "650px",
                },
          minWidth:
            micStatus === "final"
              ? "100vw"
              : {
                  xs: "300px",
                  sm: "400px",
                  md: "650px",
                },
        },
      }}
    >
      {micStatus !== "final" && micStatus !== "no-audio" && (
        <>
          <Box
            sx={{ position: "absolute", top: "58px", left: "32px", zIndex: 1 }}
          >
            <Button
              variant="text"
              onClick={handleCancel}
              sx={{
                color: customBlue.primaryBlue,
                fontSize: "16px",
                textTransform: "uppercase",
                fontWeight: 700,
                padding: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              back
            </Button>
          </Box>
          <DialogTitle
            sx={{ textAlign: "center", margin: 0, padding: 0, pt: "18px" }}
          >
            <Typography variant="pageTitle">
              Microphone{" "}
              {micStatus === "failed" ? (
                <Box
                  component="span"
                  sx={{
                    cursor: "pointer",
                  }}
                  onClick={handleSkipMicCheck}
                >
                  Check
                </Box>
              ) : (
                "Check"
              )}
            </Typography>
          </DialogTitle>
        </>
      )}

      {micStatus !== "final" &&
        (micStatus === "initial" ||
          micStatus === "recording" ||
          micStatus === "failed") && (
          <Box sx={{ textAlign: "center", pb: 0, mb: -1 }}>
            <Button
              variant="text"
              startIcon={
                <Box
                  sx={{
                    border: `2px solid ${customBlue.primaryBlue}`,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "24px",
                    height: "24px",
                    backgroundColor: "transparent",
                  }}
                >
                  <VolumeUpIcon
                    sx={{
                      fontSize: "14px",
                      width: "14px",
                      height: "14px",
                      color: customBlue.primaryBlue,
                    }}
                  />
                </Box>
              }
              onClick={handleListenToInstructions}
              sx={{
                color: customBlue.primaryBlue,
                fontSize: "16px",
                textTransform: "none",
                padding: 0,
                marging: 0,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Listen to Instructions
            </Button>
          </Box>
        )}

      {micStatus === "success" && (
        <Box sx={{ textAlign: "center", pb: 0, mb: -1, height: "28px" }}>
          {/* Spacer to match the height of "Listen to Instructions" button */}
        </Box>
      )}

      {micStatus === "final" ? (
        renderMicrophoneIcon()
      ) : micStatus === "no-audio" ? (
        <DialogContent
          sx={{
            textAlign: "center",
            py: 0,
            px: 0,
            mt: 0,
            height: "372px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            overflow: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          {renderMicrophoneIcon()}
        </DialogContent>
      ) : (
        <DialogContent
          sx={{
            textAlign: "center",
            py: 0,
            px: 4,
            mt: 0,
            pb: micStatus === "failed" ? 0 : undefined,
            height:
              micStatus === "initial" || micStatus === "recording" ? 220 : 290,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 0,
              mt:
                micStatus === "initial" || micStatus === "recording"
                  ? "20px"
                  : -2.5,
            }}
          >
            {renderMicrophoneIcon()}

            {(micStatus === "initial" ||
              micStatus === "recording" ||
              micStatus === "failed") && (
              <Box
                sx={{
                  textAlign: "center",
                  py: 2,
                  height: "120px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {micStatus !== "failed" && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        gap: 1,
                        mb: 0,
                        height: "32px",
                        minHeight: "32px",
                      }}
                    >
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mt: 0,
                          flexShrink: 0,
                        }}
                      >
                        <TapIcon svg={{ width: 16, height: 16 }} />
                      </Box>
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "18px",
                          color: "#374151",
                          fontWeight: 400,
                          lineHeight: 1.4,
                          minHeight: "20px",
                        }}
                      >
                        {micStatus === "recording" && audioDetected ? (
                          <>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Tap&nbsp;
                            </Box>
                            the button when
                            <br />
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                marginLeft: "-15px",
                              }}
                            >
                              you are done.
                            </Box>
                          </>
                        ) : (
                          <>
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Tap&nbsp;
                            </Box>
                            the microphone.
                          </>
                        )}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        height: "26px",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                      }}
                    >
                      {!(micStatus === "recording" && audioDetected) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <HumanSpeaking svg={{ width: 16, height: 16 }} />
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "18px",
                              color: "#374151",
                              fontWeight: 400,
                              lineHeight: 1.4,
                            }}
                          >
                            <Box component="span" sx={{ fontWeight: 700 }}>
                              Say:
                            </Box>{" "}
                            &quot;I am ready.&quot;
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}

                {micStatus === "failed" && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "flex-end",
                      mb: -6,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontSize: "16px",
                          color: "#DC2626",
                          fontWeight: 400,
                          lineHeight: 1.5,
                          textAlign: "center",
                        }}
                      >
                        To make sure everything works, click &quot;Allow&quot;
                        when your
                        <br />
                        browser asks for microphone access.{" "}
                        <Box
                          component="a"
                          href="https://flashlight360.zendesk.com/hc/en-us/articles/26492116030235-Enabling-the-Microphone"
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            color: "#2a2a8b",
                            textDecoration: "none",
                            fontWeight: 700,
                            "&:hover": {
                              textDecoration: "underline",
                            },
                          }}
                        >
                          Need help?
                        </Box>
                      </Typography>
                      <Button
                        onClick={handleRetry}
                        variant="contained"
                        sx={{
                          minWidth: "120px",
                          backgroundColor: customBlue.primaryBlue,
                          textTransform: "uppercase",
                          fontWeight: 700,
                          fontSize: "14px",
                          px: 3,
                          py: 1.5,
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                          "&:hover": {
                            backgroundColor: customBlue.primaryDark,
                          },
                        }}
                      >
                        try again
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
}
